import { BaseService } from '../../http-layer/service/base.service'
import { useLocation } from '../../location-layer/composables/useLocation'
import type {
  CrmContactAddressUpdate,
  CurrentUserProfile,
  CurrentUserProfileV2,
  MyCourse,
  MyGroupMembership,
  MyGroupApiRow,
} from '../types/currentUser'

/**
 * Current User Service - handles user profile and activity API calls
 * Uses different base URLs for different endpoints
 */
export class CurrentUserService extends BaseService {
  constructor() {
    super({
      // We'll handle base URLs per method since this service uses multiple APIs
      baseURL: 'REST_API_URLS',
    })
  }

  /**
   * Get current location ID
   */
  private getLocationId(): string {
    const { store: state } = useLocation()
    if (!state.locationId) throw new Error('Missing locationId')
    return state.locationId
  }

  /**
   * Get contact details from ClientPortal API
   */
  async getContactDetails(contactId: string): Promise<CurrentUserProfile> {
    const locationId = this.getLocationId()
    // Override to use CLIENTPORTAL_SERVER_URL for this endpoint
    this.config.baseURL = 'CLIENTPORTAL_SERVER_URL'

    const { data } = await this.get<CurrentUserProfile>(`/${locationId}/users/${contactId}`)

    return {
      _id: data._id,
      contactId: data?.contactId || contactId, // Use API contactId if available, fallback to parameter
      email: data?.email,
      fullName: data?.fullName,
      avatar: data?.avatar,
      bio: data?.bio,
      slug: data?.slug,
      title: data?.title,
      company: data?.company,
      location: data?.location,
      darkMode: data?.darkMode,
      termsConsent: data?.termsConsent,
      isBanned: data?.isBanned,
      languagePreference: data?.languagePreference,
      followersCount: data?.followersCount,
      profileCover: data?.profileCover,
      timeZone: data?.timeZone,
      socials: data?.socials,
      billingTokenExpiry: data?.billingTokenExpiry,
      meetingId: data?.meetingId,
      isPasswordVerified: data?.isPasswordVerified,
    }
  }

  async getUserV2Details(userId: string): Promise<CurrentUserProfileV2> {
    const locationId = this.getLocationId()
    // Override to use CLIENTPORTAL_SERVER_URL for this endpoint
    this.config.baseURL = 'CLIENTPORTAL_SERVER_URL'

    const { data } = await this.get<CurrentUserProfileV2>(`v2/${locationId}/users/${userId}`)

    return {
      _id: data._id,
      contactId: data?.contactId || userId,
      email: data?.email,
      fullName: data?.fullName,
      avatar: data?.avatar,
      slug: data?.slug,
      bio: data?.bio,
      title: data?.title,
      company: data?.company,
      location: data?.location,
      profileCover: data?.profileCover,
      darkMode: data?.darkMode,
      termsConsent: data?.termsConsent,
      isBanned: data?.isBanned,
      languagePreference: data?.languagePreference,
      followersCount: data?.followersCount,
      timeZone: data?.timeZone,
      socials: data?.socials,
      billingTokenExpiry: data?.billingTokenExpiry,
      meetingId: data?.meetingId,
      isPasswordVerified: data?.isPasswordVerified,
    }
  }

  /**
   * Update contact details
   */
  async updateContactDetails(contactId: string, profileData: Partial<CurrentUserProfile>): Promise<CurrentUserProfile> {
    const locationId = this.getLocationId()
    this.config.baseURL = 'CLIENTPORTAL_SERVER_URL'

    const { data } = await this.put<CurrentUserProfile>(`/${locationId}/users/${contactId}`, profileData)

    return {
      _id: data._id,
      contactId: data?.contactId || contactId,
      email: data?.email,
      fullName: data?.fullName,
      avatar: data?.avatar,
      bio: data?.bio,
      slug: data?.slug,
      title: data?.title,
      company: data?.company,
      location: data?.location,
      darkMode: data?.darkMode,
      termsConsent: data?.termsConsent,
      isBanned: data?.isBanned,
      languagePreference: data?.languagePreference,
      followersCount: data?.followersCount,
      profileCover: data?.profileCover,
      timeZone: data?.timeZone,
      socials: data?.socials,
      isPasswordVerified: data?.isPasswordVerified,
    }
  }

  /**
   * Update user V2 details
   */
  async updateUserV2Details(userId: string, profileData: Partial<CurrentUserProfileV2>): Promise<CurrentUserProfileV2> {
    const locationId = this.getLocationId()
    this.config.baseURL = 'CLIENTPORTAL_SERVER_URL'

    const { data } = await this.put<CurrentUserProfileV2>(`v2/${locationId}/users/${userId}`, profileData)

    return {
      _id: data._id,
      contactId: data?.contactId || userId,
      email: data?.email,
      fullName: data?.fullName,
      avatar: data?.avatar,
      slug: data?.slug,
      bio: data?.bio,
      title: data?.title,
      company: data?.company,
      location: data?.location,
      profileCover: data?.profileCover,
      darkMode: data?.darkMode,
      termsConsent: data?.termsConsent,
      isBanned: data?.isBanned,
      languagePreference: data?.languagePreference,
      followersCount: data?.followersCount,
      timeZone: data?.timeZone,
      socials: data?.socials,
      isPasswordVerified: data?.isPasswordVerified,
    }
  }

  /**
   * Get user's groups from Communities API
   */
  async getMyGroups(contactId: string): Promise<MyGroupMembership[]> {
    const locationId = this.getLocationId()
    // Override to use COMMUNITIES_SERVER_URL for this endpoint
    this.config.baseURL = 'COMMUNITIES_SERVER_URL'

    const { data } = await this.get<MyGroupApiRow[]>(`/${locationId}/groups/my-groups`, {
      params: { contactId },
    })

    // Map to typed membership objects
    return (data || []).map((myGroupRow: MyGroupApiRow): MyGroupMembership => {
      const permissions = Array.isArray(myGroupRow.permissions) ? (myGroupRow.permissions as string[]) : []
      return {
        groupId: String(myGroupRow.groupId || myGroupRow._id || myGroupRow.id || ''),
        role: myGroupRow.role,
        permissions,
        status: myGroupRow.status,
        lastVisitedAt: myGroupRow.lastVisitedAt,
        pinned: Boolean(myGroupRow.pinned),
      }
    })
  }

  async getGokollabUser(offerLocationId: string): Promise<{ [key: string]: unknown }> {
    const locationId = this.getLocationId()
    this.config.baseURL = 'GOKOLLAB_SERVER_URL'

    const { data } = await this.get(`/${locationId}/users/${offerLocationId}`)
    return data as { [key: string]: unknown }
  }

  async getWhiteLabelUserContact(
    entityLocationId: string,
    signal?: AbortSignal,
    groupId?: string
  ): Promise<{ contactId: string; locationId: string }> {
    const locationId = this.getLocationId()
    this.config.baseURL = 'GOKOLLAB_SERVER_URL'

    const { data } = await this.get<{ contactId: string; locationId: string }>(`/${locationId}/users/wl-user-contact/${entityLocationId}`, {
      signal,
      ...(groupId ? { params: { groupId } } : {}),
    })
    return data
  }

  /**
   * Persist billing address on the buyer's CRM contact. The clientportal
   * interceptor rewrites `clientclub/v2/...` → `clientclub/...` for the
   * leadership API path. `locationId` is explicit (not read from store) so
   * the call always targets the sub-account whose CRM owns the contact.
   */
  async updateCrmContactAddress(locationId: string, contactId: string, address: CrmContactAddressUpdate): Promise<void> {
    this.config.baseURL = 'CLIENTPORTAL_SERVER_URL'
    await this.patch(`/${locationId}/users/crm-contact/${contactId}`, address)
  }

  /**
   * Whether the contact is ADMIN or OWNER of at least one group at this location.
   * Matches legacy clientportal `CommunitiesService.isContactAdminOrOwnerOfAnyGroup`.
   */
  async fetchIsContactAdminOrOwnerOfAnyGroup(contactId: string): Promise<boolean> {
    const locationId = this.getLocationId()
    this.config.baseURL = 'COMMUNITIES_SERVER_URL'

    const { data } = await this.get<{ isAdminOrOwner?: boolean }>(`/${locationId}/users/${contactId}/is-group-admin-or-owner`)

    return Boolean(data?.isAdminOrOwner)
  }

  /**
   * Change user password
   */
  async changePassword(passwordData: {
    currentPassword: string
    newPassword: string
    confirmPassword: string
    contactId?: string
    revokeAllSessions?: boolean
  }): Promise<void> {
    const { contactId } = passwordData
    const locationId = this.getLocationId()
    this.config.baseURL = 'CLIENTPORTAL_SERVER_URL'

    if (!contactId) throw new Error('Contact ID not found')

    await this.post(`/v2/${locationId}/users/change-password/${contactId}`, {
      currentPassword: passwordData.currentPassword,
      newPassword: passwordData.newPassword,
      confirmPassword: passwordData.confirmPassword,
      revokeAllSessions: passwordData.revokeAllSessions,
    })
  }
}
